import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Loader2, TrendingUp, TrendingDown, BarChart3, RefreshCw } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BacktestSection({ embedded = false }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [historicalStats, setHistoricalStats] = useState(null);
  
  const [strategy, setStrategy] = useState('value_betting');
  const [minOdds, setMinOdds] = useState('1.30');
  const [maxOdds, setMaxOdds] = useState('4.00');
  const [minValueEdge, setMinValueEdge] = useState('3.0');
  const [stakeMode, setStakeMode] = useState('flat');
  const [bankroll, setBankroll] = useState('100');

  useEffect(() => {
    fetchStrategies();
    fetchHistoricalStats();
  }, []);

  const fetchStrategies = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/backtest/strategies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setStrategies(data.strategies || []);
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  };

  const fetchHistoricalStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/backtest/historical-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setHistoricalStats(data);
    } catch (error) {
      console.error('Error fetching historical stats:', error);
    }
  };

  const runBacktest = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        strategy,
        min_odds: minOdds,
        max_odds: maxOdds,
        min_value_edge: minValueEdge,
        stake_mode: stakeMode,
        bankroll
      });
      
      const res = await fetch(`${API_URL}/api/backtest/run?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      setResults(data);
      toast.success('Backtest completado');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error ejecutando backtest');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="backtest-section">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-white">Backtesting</h3>
          <p className="text-xs text-zinc-500">
            {historicalStats ? `${historicalStats.total_available?.toLocaleString()} datos históricos` : 'Cargando...'}
          </p>
        </div>
      </div>

      {/* Parameters - Compact */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="pt-4 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div>
              <label className="text-[10px] text-zinc-500 mb-1 block">Estrategia</label>
              <Select value={strategy} onValueChange={setStrategy}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {strategies.map(s => (
                    <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 mb-1 block">Cuota Min</label>
              <Input
                type="number"
                step="0.1"
                value={minOdds}
                onChange={e => setMinOdds(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs"
              />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 mb-1 block">Cuota Max</label>
              <Input
                type="number"
                step="0.1"
                value={maxOdds}
                onChange={e => setMaxOdds(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs"
              />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 mb-1 block">Value Edge %</label>
              <Input
                type="number"
                step="0.5"
                value={minValueEdge}
                onChange={e => setMinValueEdge(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs"
              />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 mb-1 block">Stake</label>
              <Select value={stakeMode} onValueChange={setStakeMode}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flat">Flat</SelectItem>
                  <SelectItem value="kelly">Kelly</SelectItem>
                  <SelectItem value="percentage">%</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 mb-1 block">&nbsp;</label>
              <Button
                onClick={runBacktest}
                disabled={loading}
                className="w-full h-9 bg-[#6366F1] hover:bg-[#5558E3]"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
                <span className="ml-1 text-xs">Run</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-zinc-500 uppercase">Win Rate</p>
                <p className={`text-xl font-bold ${results.summary.win_rate >= 55 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                  {results.summary.win_rate}%
                </p>
                <p className="text-[10px] text-zinc-400">{results.summary.wins}W / {results.summary.losses}L</p>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-zinc-500 uppercase">ROI</p>
                <p className={`text-xl font-bold ${results.summary.roi >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                  {results.summary.roi >= 0 ? '+' : ''}{results.summary.roi}%
                </p>
                <p className="text-[10px] text-zinc-400">
                  {results.summary.total_profit >= 0 ? '+' : ''}{results.summary.total_profit}u
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-zinc-500 uppercase">Total Picks</p>
                <p className="text-xl font-bold text-white">{results.summary.total_picks}</p>
                <p className="text-[10px] text-zinc-400">@{results.performance?.avg_odds || '?'} avg</p>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-zinc-500 uppercase">Max Drawdown</p>
                <p className={`text-xl font-bold ${results.risk?.max_drawdown_pct <= 20 ? 'text-[#EAB308]' : 'text-[#F43F5E]'}`}>
                  -{results.risk?.max_drawdown_pct || 0}%
                </p>
                <p className="text-[10px] text-zinc-400">-{results.risk?.max_drawdown || 0}u</p>
              </CardContent>
            </Card>
          </div>

          {/* Equity Curve */}
          {results.equity_curve?.length > 0 && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="text-sm text-white">Curva de Equity</CardTitle>
              </CardHeader>
              <CardContent className="pb-3">
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={results.equity_curve}>
                      <defs>
                        <linearGradient id="colorEquityEmbed" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="date" 
                        stroke="#52525b"
                        tick={{ fill: '#71717a', fontSize: 9 }}
                        tickFormatter={(v) => v?.substring(5, 10)}
                      />
                      <YAxis 
                        stroke="#52525b"
                        tick={{ fill: '#71717a', fontSize: 9 }}
                        width={35}
                      />
                      <Tooltip
                        contentStyle={{ 
                          backgroundColor: '#18181b', 
                          border: '1px solid #27272a',
                          borderRadius: '8px',
                          fontSize: '11px'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="equity" 
                        stroke="#6366F1" 
                        fillOpacity={1} 
                        fill="url(#colorEquityEmbed)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* By Market & League */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="text-sm text-white">Por Mercado</CardTitle>
              </CardHeader>
              <CardContent className="pb-3">
                <div className="space-y-2">
                  {Object.entries(results.by_market || {}).slice(0, 5).map(([market, data]) => (
                    <div key={market} className="flex items-center justify-between text-xs">
                      <div>
                        <span className="text-white">{market}</span>
                        <span className="text-zinc-500 ml-1">({data.picks})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={data.win_rate >= 55 ? "default" : "destructive"} className="text-[10px]">
                          {data.win_rate}%
                        </Badge>
                        <span className={`font-medium ${data.profit >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                          {data.profit >= 0 ? '+' : ''}{data.profit}u
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="text-sm text-white">Por Liga</CardTitle>
              </CardHeader>
              <CardContent className="pb-3">
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(results.by_league || {}).slice(0, 4).map(([league, data]) => (
                    <div key={league} className="bg-zinc-800/50 rounded-lg p-2">
                      <p className="text-[10px] text-zinc-500 uppercase truncate">{league}</p>
                      <p className="text-sm font-bold text-white">{data.win_rate}%</p>
                      <p className={`text-[10px] ${data.profit >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                        {data.profit >= 0 ? '+' : ''}{data.profit}u
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* No Results */}
      {!results && !loading && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-8 text-center">
            <BarChart3 className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
            <p className="text-zinc-400 text-sm">Configura los parámetros y ejecuta el backtest</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
